import { useState, useEffect, useRef } from "react";
import {
  Stack,
  Typography,
  Box,
  ListItemButton,
  ListItemText,
} from "@mui/material";

const handleScrollToTop = (
  id: string,
  isScrolled?: boolean,
  isPublicPage?: boolean,
  isDemo?: boolean,
) => {
  const el = document.getElementById(id);
  if (el) {
    const extra = isDemo ? 36 : 0;
    const y =
      el.getBoundingClientRect().top +
      window.scrollY -
      (isPublicPage
        ? isScrolled
          ? 156
          : 348
        : isScrolled
          ? 146 + extra
          : 322 + extra);

    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

const scrollThreshold = 54;

export const PlaybookNavigationBlock = ({
  isPublicPage,
  topicsList,
  isDemo,
}: {
  isPublicPage?: boolean;
  topicsList: string[];
  isDemo?: boolean;
}) => {
  const [selected, setSelected] = useState<string>("");
  const [isSticky, setIsSticky] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      const delta = e.deltaY;
      const up = delta < 0;
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const offsetHeight = el.offsetHeight;

      const atTop = scrollTop === 0;
      const atBottom = scrollTop + offsetHeight >= scrollHeight;

      if ((up && atTop) || (!up && atBottom)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });

    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > scrollThreshold) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box minWidth="260px">
      <Box
        ref={scrollRef}
        sx={{
          width: "260px",
          borderRight: "1px solid #ddd",
          position: isSticky ? "sticky" : "relative",
          top: isSticky
            ? `${isPublicPage ? 142 : isDemo ? 168 : 140}px`
            : "auto",
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack>
          {topicsList.map((topic) => (
            <Box key={topic}>
              <ListItemButton
                onClick={() => {
                  setSelected(topic);
                  handleScrollToTop(topic, isSticky, isPublicPage, isDemo);
                }}
                sx={{
                  gap: "12px",
                  justifyContent: "flex-start",
                  backgroundColor:
                    selected === topic ? "#F1F6FF" : "transparent",
                  padding: 0,
                  paddingLeft: "16px",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "5px",
                    height: "100%",
                    backgroundColor:
                      selected === topic ? "#0860A8" : "transparent",
                  },
                }}
              >
                <ListItemText
                  onClick={(e) => {
                    e.stopPropagation();
                    handleScrollToTop(topic, isSticky, isPublicPage, isDemo);
                    setSelected(topic);
                  }}
                  sx={{ padding: "10px 24px 10px 0", margin: 0 }}
                  primary={
                    <Typography fontSize="14px" lineHeight="20px">
                      {topic}
                    </Typography>
                  }
                />
              </ListItemButton>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};
